import { CommonFormDialog } from "@/components/common/CommonFormDialog";
import { CommonPagination } from "@/components/common/CommonPagination";
import { ConfirmationDialog } from "@/components/common/ConfirmationDialog";
import Loading from "@/components/common/Loading";
import CreateAdminPaymentChannelForm from "@/components/forms/CreateAdminPaymentChannelForm";
import CreateMerchantPaymentChannelForm from "@/components/forms/CreateMerchantPaymentChannelForm";
import { UpdateAdminPaymentChannelForm } from "@/components/forms/UpdateAdminPaymentChannelForm";
import { UpdateMerchantPaymentChannelForm } from "@/components/forms/UpdateMerchantPaymentChannelForm";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { ROLE } from "@/constant";
import { QUERY_KEY } from "@/constant/queryKey";
import { useDeletePaymentChannel } from "@/hooks/services/use-payment-channel";
import { useDebounce } from "@/hooks/use-debounce";
import { usePagination } from "@/hooks/use-pagination";
import { useUrlParams } from "@/hooks/use-url-params";
import {
  AdminPaymentChannel,
  MerchantPaymentChannel,
  PAYMENT_CHANNEL_ENDPOINT,
} from "@/services/core/payment-channel.api";
import { useDialogStore } from "@/store/dialog-store";
import { Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const PaymentChannelListsPage = () => {
  const { params, updateParams } = useUrlParams();
  const [searchInput, setSearchInput] = useState(params.q);
  const debouncedSearch = useDebounce(searchInput, 500);

  const {
    data: paymentChannels,
    isLoading,
    isError,
    meta,
    setPage,
    setSearch,
    setLimit,
  } = usePagination<AdminPaymentChannel>({
    endpoint: PAYMENT_CHANNEL_ENDPOINT,
    queryKey: [QUERY_KEY.PAYMENT_CHANNELS],
    initialParams: {
      currentPage: params.currentPage,
      pageSize: params.pageSize,
      s: params.s,
      q: params.q,
    },
  });

  const [selectedAdminChannel, setSelectedAdminChannel] =
    useState<AdminPaymentChannel | null>(null);
  const [selectedMerchantChannel, setSelectedMerchantChannel] =
    useState<MerchantPaymentChannel | null>(null);

  const {
    createDialog,
    updateDialog,
    deleteDialog,
    openCreateDialog,
    closeCreateDialog,
    openUpdateDialog,
    closeUpdateDialog,
    openDeleteDialog,
    closeDeleteDialog,
  } = useDialogStore();

  const deleteMutation = useDeletePaymentChannel();

  // const handleMerchantEdit = (channel: MerchantPaymentChannel) => {
  //   setSelectedMerchantChannel(channel);
  //   closeUpdateDialog();
  // };

  // const handleMerchantDeleteSubmit = () => {
  //   console.log(
  //     `Deleting item with ID: ${selectedMerchantChannel?.paymentChannelId}`,
  //   );
  // };

  const handleAdminEdit = (channel: AdminPaymentChannel) => {
    setSelectedAdminChannel(channel);
    openUpdateDialog();
  };

  const handleAdminDeleteSubmit = async () => {
    console.log(`Deleting item with ID: ${selectedAdminChannel?.id}`);
    if (!selectedAdminChannel) return;
    try {
      await deleteMutation.mutateAsync(selectedAdminChannel.id);

      toast.success("Delete payment channel successful!");
    } catch (error) {
      console.error("Failed to create payment channel:", error);
    }
  };

  // Filter the payload based on search query
  // const filteredPayload = paymentChannels.filter(
  //   (item) =>
  //     item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     item.description.toLowerCase().includes(searchQuery.toLowerCase()),
  // );

  // Get available channel IDs (those not already in use)
  // const usedChannelIds = new Set(
  //   paymentChannels.map((item) => item.paymentChannelId),
  // );
  // const availableChannelIds = Array.from({ length: 15 }, (_, i) =>
  //   (i + 1).toString(),
  // ).filter((id) => !usedChannelIds.has(id));

  const handlePageChange = (page: number) => {
    updateParams({ currentPage: page });
    setPage(page);
  };

  const handleLimitChange = (limit: number) => {
    updateParams({ pageSize: limit, currentPage: 1 });
    setLimit(limit);
  };

  const handleSortChange = (sort: string) => {
    console.log("sort", sort);
    updateParams({ s: sort, currentPage: 1 });
  };

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    setSearchInput(value);

    if (trimmedValue !== params.q) {
      updateParams({ q: trimmedValue, currentPage: 1 });
    }
  };

  useEffect(() => {
    updateParams({ q: debouncedSearch, currentPage: 1 });
    setSearch(debouncedSearch || "");
    setPage(1);
  }, [debouncedSearch]);

  return (
    <div className="w-full space-y-4">
      <div className="flex w-full items-center justify-between gap-4 sm:flex-row sm:items-center">
        <div className="relative w-1/2">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search channels..."
            className="pl-8"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="text-muted-foreground hover:text-foreground absolute top-2.5 right-2 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
            >
              <X className="h-4 w-4 cursor-pointer" />
              <span className="sr-only">Clear search</span>
            </button>
          )}
        </div>

        <div className="flex w-full items-center justify-end gap-2">
          <Button onClick={openCreateDialog} className="cursor-pointer">
            <Plus className="mr-2 h-4 w-4" /> Create Payment Channel
          </Button>

          <Select defaultValue={params.s} onValueChange={handleSortChange}>
            <SelectTrigger className="w-42">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tableId:asc">ID (Asc)</SelectItem>
              <SelectItem value="tableId:desc">ID (Desc)</SelectItem>
              <SelectItem value="name:asc">Name (A-Z)</SelectItem>
              <SelectItem value="name:desc">Name (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      ) : isError ? (
        <div className="py-8 text-center text-red-500">
          Error loading payment channels
        </div>
      ) : (
        <>
          {paymentChannels.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">
                      Description
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      Status
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentChannels.map((item, idx) => {
                    const tableId =
                      (meta.currentPage - 1) * meta.pageSize + idx + 1;
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{tableId}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          {item.description}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge
                            variant={item.active ? "success" : "destructive"}
                          >
                            {item.active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleAdminEdit(item)}
                              aria-label={`Edit ${item.name}`}
                              className="cursor-pointer"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="icon"
                              className="cursor-pointer"
                              onClick={() => {
                                openDeleteDialog();
                                setSelectedAdminChannel(item);
                              }}
                              aria-label={`Delete ${item.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex h-42 items-center justify-center">
              <p>There is no data</p>
            </div>
          )}

          {meta && (
            <CommonPagination
              page={meta.currentPage}
              totalPages={meta.totalPages}
              limit={meta.pageSize}
              hasNextPage={meta.hasNextPage}
              hasPrevPage={meta.hasPreviousPage}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100]}
              showLimitSelector={true}
            />
          )}
        </>
      )}

      {/* Create Dialog */}
      <CommonFormDialog
        title="Create Payment Channel"
        description="Fill in the details to create a new payment channel."
        isOpen={createDialog}
        onOpenChange={closeCreateDialog}
      >
        {ROLE === "admin" ? (
          <CreateAdminPaymentChannelForm />
        ) : (
          <CreateMerchantPaymentChannelForm
          // availableChannelIds={availableChannelIds}
          />
        )}
      </CommonFormDialog>

      {/* Update Dialog */}
      <CommonFormDialog
        title="Update Payment Channel"
        description="Update the details of this payment channel."
        isOpen={updateDialog}
        onOpenChange={closeUpdateDialog}
      >
        {ROLE === "admin"
          ? selectedAdminChannel && (
              <UpdateAdminPaymentChannelForm
                selectedAdminChannel={selectedAdminChannel}
                setSelectedAdminChannel={setSelectedAdminChannel}
                initialData={{
                  id: selectedAdminChannel.id,
                  name: selectedAdminChannel.name,
                  description: selectedAdminChannel.description,
                  keyCheck: selectedAdminChannel.keyCheck,
                  active: selectedAdminChannel.active,
                }}
              />
            )
          : selectedMerchantChannel && (
              <UpdateMerchantPaymentChannelForm
                selectedMerchantChannel={selectedMerchantChannel}
                setSelectedMerchantChannel={setSelectedMerchantChannel}
                initialData={{
                  paymentChannelId:
                    selectedMerchantChannel.paymentChannelId.toString(),
                  key: {
                    additionalProp1:
                      selectedMerchantChannel.key.additionalProp1 || "",
                    additionalProp2:
                      selectedMerchantChannel.key.additionalProp2 || "",
                    additionalProp3:
                      selectedMerchantChannel.key.additionalProp3 || "",
                  },
                  active: selectedMerchantChannel.active,
                }}
              />
            )}
      </CommonFormDialog>

      {/* Delete Dialog For Admin*/}
      <ConfirmationDialog
        open={deleteDialog}
        onOpenChange={closeDeleteDialog}
        onConfirm={handleAdminDeleteSubmit}
        confirmText={deleteMutation.isPending ? "Deleting..." : "Delete"}
        title="Deleting Payment Channel?"
        description="This action cannot be undone. This will permanently delete your payment channel from our servers."
        loading={deleteMutation.isPending}
      />
    </div>
  );
};

export default PaymentChannelListsPage;
